import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { laseaAuthService } from '../../services/laseaAuthService';
import ThemeToggle from '../../components/ui/ThemeToggle';


export default function AdminLogin() {
  const navigate = useNavigate();
  const { signIn, signOut } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  /**
   * Directly fetches the user role from the DB.
   * This avoids race conditions where the AuthContext subscription
   * gets aborted by React navigation/unmount before completing.
   */


  const handleAdminLogin = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Step 1: Sign in with Supabase Auth
      const result = await signIn(email, password);

      if (!result?.user) {
        throw new Error('Authentication failed. Please try again.');
      }

      // Step 2: Role is returned directly from the Worker login response
      const role = result.user.role;
      console.log('✅ Sign in successful, role:', role);

      // Step 3: Verify admin access
      if (!role) {
        await signOut();
        throw new Error('Unable to verify user role. Please try again.');
      }

      if (role !== 'admin') {
        await signOut();
        throw new Error('Access denied. Admin credentials required.');
      }

      console.log('✅ Admin access confirmed, navigating to dashboard');
      navigate('/admin-dashboard');

    } catch (err) {
      console.error('Admin login error:', err);

      // Provide more helpful error messages
      const msg = err?.message || '';
      let errorMessage = msg || 'Login failed. Please check your credentials.';

      if (msg.includes('Connection error') || msg.includes('Unable to connect') || msg.includes('Load failed')) {
        errorMessage = 'Connection error: Unable to reach the server. Please check your internet connection and try again.';
      } else if (msg.includes('Invalid login credentials')) {
        errorMessage = 'Invalid email or password. Please try again.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      await laseaAuthService?.resetPassword(email);
      setResetEmailSent(true);
    } catch (err) {
      setError(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center min-h-screen bg-gray-900 p-6">
      {/* Theme Toggle in Top Corner - Hidden since theme is locked */}
      <div className="absolute top-6 right-6 z-10 hidden">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-md bg-gray-800 rounded-lg shadow-md p-8">
        <h2 className="text-2xl font-bold text-center mb-6 text-white">
          {showForgotPassword ? 'Reset Password' : 'Admin Login'}
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-900 border border-red-700 text-red-200 rounded">
            {error}
          </div>
        )}

        {resetEmailSent && (
          <div className="mb-4 p-3 bg-green-900 border border-green-700 text-green-200 rounded">
            Password reset email sent! Check your inbox for instructions.
          </div>
        )}

        {!showForgotPassword ? (
          <form onSubmit={handleAdminLogin}>
            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={loading}
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e?.target?.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={loading}
              />
            </div>

            <div className="mb-6 text-right">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-blue-400 hover:underline"
              >
                Forgot Password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleForgotPassword}>
            <p className="text-sm text-gray-400 mb-4">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            <div className="mb-6">
              <label className="block text-gray-300 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 mb-3"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(false);
                setResetEmailSent(false);
                setError('');
              }}
              className="w-full text-gray-400 hover:text-gray-200"
            >
              Back to Login
            </button>
          </form>
        )}
      </div>
    </div>
  );
}